import React, { useEffect, useState, useCallback } from 'react';
import './LightboxImage.css';

/**
 * Clickable image (or custom trigger) that opens a full-screen lightbox.
 * @param {string} src - Thumbnail or same as full image
 * @param {string} [fullSrc] - Larger/original URL (defaults to src)
 */
export function LightboxImage({
  src,
  fullSrc,
  alt = '',
  className = '',
  imgClassName = '',
  children,
}) {
  const [open, setOpen] = useState(false);
  const large = fullSrc || src;

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  if (!src) {
    return children || null;
  }

  const trigger = children ? (
    <button
      type="button"
      className={`lux-lightbox-trigger lux-lightbox-trigger--unstyled ${className}`.trim()}
      onClick={() => setOpen(true)}
      aria-label={alt ? `Enlarge: ${alt}` : 'View larger image'}
    >
      {children}
    </button>
  ) : (
    <button
      type="button"
      className={`lux-lightbox-trigger ${className}`.trim()}
      onClick={() => setOpen(true)}
      aria-label={alt ? `Enlarge: ${alt}` : 'View larger image'}
    >
      <img src={src} alt={alt} className={imgClassName} />
    </button>
  );

  return (
    <>
      {trigger}
      {open ? (
        <div
          className="lux-lightbox-backdrop"
          role="presentation"
          onClick={close}
        >
          <div
            className="lux-lightbox-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={alt || 'Enlarged image'}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="lux-lightbox-close"
              onClick={close}
              aria-label="Close"
            >
              ×
            </button>
            <img src={large} alt={alt} className="lux-lightbox-img" />
            <a
              href={large}
              target="_blank"
              rel="noopener noreferrer"
              className="lux-lightbox-open-tab"
            >
              {large !== src ? 'Open full size in new tab' : 'Open in new tab'}
            </a>
          </div>
        </div>
      ) : null}
    </>
  );
}
